import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';

import { CameraPermission, CameraPermissionStatus, Platform } from '@type';

import { logError } from '../error';

export const getPlatform = (): Platform => {
  return Capacitor.getPlatform() as Platform;
};
export const isIos = () => getPlatform() === 'ios';
export const isAndroid = () => getPlatform() === 'android';
export const isWeb = () => getPlatform() === 'web';

export const isNativeDevice = Capacitor.isNativePlatform();

export const isAndroidBrowser = /android|Windows/i.test(navigator.userAgent);

export const isIOSBrowser = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);

export const isMobileBrowser = /iPad|iPhone|iPod|android/i.test(
  navigator.userAgent
);

export const getMapsLink = (address?: string) => {
  const provider = isIos() ? 'apple' : 'google';
  return `https://maps.${provider}.com${address ? `?daddr=${address}` : ''}`;
};

export const openDeviceSettings = async () => {
  await NativeSettings.open({
    optionAndroid: AndroidSettings.ApplicationDetails,
    optionIOS: IOSSettings.App,
  });
};

export const getCameraPermission = async () =>
  (await Camera.checkPermissions()).camera;

export const FACING_MODE: MediaTrackConstraintSet['facingMode'] = 'environment';

export const requestCameraPermission = async () => {
  const IDEAL_WIDTH = 1170;
  const IDEAL_HEIGHT = 2532;

  try {
    if (isNativeDevice) {
      const permission = await Camera.requestPermissions({
        permissions: ['camera'],
      });
      return (
        permission.camera === CameraPermission.Granted ||
        permission.camera === CameraPermission.Prompt
      );
    }

    return (
      await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: IDEAL_WIDTH },
          height: { ideal: IDEAL_HEIGHT },
          facingMode: FACING_MODE,
        },
      })
    ).active;
  } catch (e) {
    logError(e);
    return false;
  }
};

export const checkCameraPermission = async () => {
  const isPermissionGranted = await requestCameraPermission();
  const permission = isPermissionGranted
    ? CameraPermissionStatus.Granted
    : CameraPermissionStatus.Denied;
  return permission;
};
